import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { scrollToId } from "../../lib/motion";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Menu", id: "menu" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "FAQ", id: "faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 2.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/80 backdrop-blur-xl border-b border-border py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("hero")}
          className="flex items-center gap-2 group"
        >
          {scrolled ? (
            <img
              src="/okome-logo.png"
              alt="OKOME Catering"
              className="h-12 w-auto"
            />
          ) : (
            <>
              <span className="font-serif text-2xl md:text-3xl tracking-tight text-cream">
                OKOME
              </span>
              <span className="label-caps text-gold hidden sm:inline">Catering</span>
            </>
          )}
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`text-sm transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all hover:after:w-full ${
                scrolled ? "text-ink/70 hover:text-ink" : "text-cream/80 hover:text-cream"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-order-btn"
            onClick={() => go("contact")}
            className={`hidden md:inline-flex items-center rounded-full px-6 py-2.5 text-sm transition-colors duration-300 ${
              scrolled
                ? "bg-charcoal text-cream hover:bg-sage"
                : "bg-gold text-charcoal hover:bg-cream"
            }`}
          >
            Order Now
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className={`md:hidden p-2 ${scrolled ? "text-ink" : "text-cream"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-ink"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-order-btn"
                onClick={() => go("contact")}
                className="mt-2 rounded-full bg-charcoal px-6 py-3 text-cream text-center"
              >
                Order Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
